// How much time the user can give a goal. Pure TS + validator, shared by the
// planner prompts, the goal node and the web/native pickers.

import { type Infer, v } from 'convex/values'

export const timeBudgetValidator = v.object({
  minutesPerWeek: v.number(),
  pattern: v.union(v.literal('daily'), v.literal('weekly'), v.literal('weekends')),
})

export type TimeBudget = Infer<typeof timeBudgetValidator>
export type TimeBudgetPattern = TimeBudget['pattern']

export const TIME_BUDGET_CHOICES = [
  'auto',
  '10m',
  '20m',
  '30m',
  '1h',
  '2h',
  'fewHoursWeek',
  'weekends',
] as const
export type TimeBudgetChoice = (typeof TIME_BUDGET_CHOICES)[number]

const CHOICE_BUDGET: Record<Exclude<TimeBudgetChoice, 'auto'>, TimeBudget> = {
  '10m': { minutesPerWeek: 70, pattern: 'daily' },
  '20m': { minutesPerWeek: 140, pattern: 'daily' },
  '30m': { minutesPerWeek: 210, pattern: 'daily' },
  '1h': { minutesPerWeek: 420, pattern: 'daily' },
  '2h': { minutesPerWeek: 840, pattern: 'daily' },
  fewHoursWeek: { minutesPerWeek: 180, pattern: 'weekly' },
  weekends: { minutesPerWeek: 240, pattern: 'weekends' },
}

// Estimated-length chips for a single task.
export const TASK_DURATIONS = [5, 10, 15, 20, 30, 45, 60, 90, 120] as const

const MIN_PER_WEEK = 10
const MAX_PER_WEEK = 7 * 12 * 60

export function budgetForChoice(choice?: TimeBudgetChoice | null): TimeBudget | undefined {
  return choice && choice !== 'auto' ? CHOICE_BUDGET[choice] : undefined
}

// The chip a stored budget matches; null for a custom one (e.g. set by the coach).
export function choiceForBudget(budget?: TimeBudget | null): TimeBudgetChoice | null {
  if (!budget) return 'auto'
  const hit = (Object.keys(CHOICE_BUDGET) as Exclude<TimeBudgetChoice, 'auto'>[]).find(
    (k) =>
      CHOICE_BUDGET[k].pattern === budget.pattern &&
      CHOICE_BUDGET[k].minutesPerWeek === budget.minutesPerWeek,
  )
  return hit ?? null
}

// Clamps untrusted input (client args, coach tool calls); undefined = let AI decide.
export function normalizeTimeBudget(raw: unknown): TimeBudget | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const { minutesPerWeek, pattern } = raw as { minutesPerWeek?: unknown; pattern?: unknown }
  const mins = Number(minutesPerWeek)
  if (!Number.isFinite(mins) || mins <= 0) return undefined
  return {
    minutesPerWeek: Math.round(Math.min(MAX_PER_WEEK, Math.max(MIN_PER_WEEK, mins))),
    pattern: pattern === 'weekly' || pattern === 'weekends' ? pattern : 'daily',
  }
}

// Minutes in one sitting: a day's share, a weekend day's, or one of ~3 weekly sessions.
export function sessionMinutes(budget: TimeBudget): number {
  const per =
    budget.pattern === 'daily'
      ? budget.minutesPerWeek / 7
      : budget.pattern === 'weekends'
        ? budget.minutesPerWeek / 2
        : budget.minutesPerWeek / 3
  return Math.max(5, Math.round(per / 5) * 5)
}

export function timeBudgetEffort(budget: TimeBudget): 'low' | 'medium' | 'high' {
  return budget.minutesPerWeek <= 150 ? 'low' : budget.minutesPerWeek <= 480 ? 'medium' : 'high'
}

function describe(budget: TimeBudget): string {
  const session = sessionMinutes(budget)
  if (budget.pattern === 'daily') return `about ${session} minutes a day`
  if (budget.pattern === 'weekends')
    return `weekends only — about ${session} minutes on each of Saturday and Sunday`
  return `about ${budget.minutesPerWeek} minutes a week, in a few sessions of roughly ${session} minutes`
}

// Plan-prompt block; empty when the user let the AI decide, so prompts are unchanged.
export function timeBudgetPrompt(budget?: TimeBudget | null): string {
  if (!budget) return ''
  const session = sessionMinutes(budget)
  const habitRule =
    budget.pattern === 'weekends'
      ? `Habits use cadence "weekend" and their "minutes" fit within ${session} minutes.`
      : budget.pattern === 'weekly'
        ? `Habits are light (cadence "weekly" or "days" on 2-3 days) and their "minutes" fit within ${session} minutes.`
        : `Habits' "minutes" fit within the ${session} minutes a day, together with that day's task.`
  return `TIME BUDGET — THE USER CHOSE THIS, RESPECT IT: they can give this goal ${describe(budget)} (${budget.minutesPerWeek} minutes a week in total, habits included).
- Size every task's "minutes" to fit one ${session}-minute sitting. Work bigger than one sitting becomes separate sessions; never overload a single task.
- ${habitRule}
- Pace milestones and targets for ${budget.minutesPerWeek} minutes a week: if the goal is ambitious for that time, stretch the timeline and keep each milestone smaller rather than packing in more work. If a deadline is tight for this time, keep the weekly time fixed and scope each milestone down instead of exceeding it.`
}

// One-line version for the smaller replan prompts.
export function timeBudgetReplanLine(budget?: TimeBudget | null): string {
  if (!budget) return ''
  return `Time budget: ${describe(budget)} (${budget.minutesPerWeek} min/week). Each task must fit one ${sessionMinutes(budget)}-minute sitting; give its honest "minutes".`
}

// Optional create_goal tool params, shared by the text coach and both voice-call clients.
export const TIME_BUDGET_TOOL_PROPS = {
  minutesPerWeek: {
    type: 'number',
    description:
      'Optional: the time a week the user said they can give this goal, in minutes (20 min a day → 140, 1 hour a day → 420, "a few hours a week" → 180). Omit if they did not say.',
  },
  timePattern: {
    type: 'string',
    enum: ['daily', 'weekly', 'weekends'],
    description:
      'Optional, with minutesPerWeek: daily = a bit every day, weekly = a few sessions a week, weekends = weekends only.',
  },
} as const

export const TIME_BUDGET_COACH_RULE =
  'If the user has not said how much time they can give a new goal, you may ask once, briefly ("How much time can you give this?"), and pass the answer to create_goal as minutesPerWeek/timePattern. If they already said, pass it without asking. Never block on it: if they want the goal now, skip the question or say "you decide", create it without these fields.'

export function timeBudgetFromToolArgs(args: Record<string, unknown>): TimeBudget | undefined {
  return normalizeTimeBudget({ minutesPerWeek: args.minutesPerWeek, pattern: args.timePattern })
}
